import { create } from 'zustand';
import { supabase, auth, isSupabaseConfigured } from '../lib/supabase';
import type { User } from '../types';
import {
  setUserContext,
  clearUserContext,
  addBreadcrumb,
  captureException,
  captureMessage,
  Sentry,
} from '../lib/sentry';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  profileSelected: boolean;
  isSupabaseConfigured: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, metadata?: any) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  selectProfile: (profileType: string) => Promise<void>;
  fetchUser: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  profileSelected: false,
  isSupabaseConfigured: isSupabaseConfigured(),

  /**
   * Connexion utilisateur
   */
  login: async (email: string, password: string) => {
    set({ isLoading: true });
    addBreadcrumb({ category: 'auth', message: `Tentative de connexion: ${email}`, level: 'info' });
    try {
      const { data, error } = await auth.signIn(email, password);
      if (error) throw error;
      await get().fetchUser();
      captureMessage(`Connexion réussie: ${email}`, 'info');
    } catch (error) {
      captureException(error as Error, { phase: 'login', email });
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Inscription nouvel utilisateur
   */
  register: async (email: string, password: string, metadata: any = {}) => {
    set({ isLoading: true });
    addBreadcrumb({ category: 'auth', message: `Création de compte: ${email}`, level: 'info' });
    try {
      const { data, error } = await auth.signUp(email, password, metadata);
      if (error) throw error;

      if (data.user && !data.user.email_confirmed_at) {
        // Email de confirmation requis
        set({
          user: null,
          isAuthenticated: false,
          profileSelected: false,
          isLoading: false,
        });
      } else if (data.user) {
        // Création de profil utilisateur par défaut
        const { error: profileError } = await supabase.from('users').insert({
          id: data.user.id,
          profile_type: 'PORTEUR',
          full_name: metadata.full_name || null,
          phone: metadata.phone || null,
          organization: metadata.organization || null,
        });
        if (profileError) throw profileError;

        await get().fetchUser();
        captureMessage(`Inscription réussie: ${email}`, 'info');
      }
    } catch (error) {
      captureException(error as Error, { phase: 'register', email });
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Déconnexion utilisateur
   */
  logout: async () => {
    addBreadcrumb({ category: 'auth', message: 'Déconnexion utilisateur', level: 'info' });
    try {
      if (isSupabaseConfigured()) {
        await auth.signOut();
      }
      clearUserContext();
      set({
        user: null,
        isAuthenticated: false,
        profileSelected: false,
        isLoading: false,
      });
      captureMessage('Utilisateur déconnecté', 'info');
    } catch (error) {
      captureException(error as Error, { phase: 'logout' });
      throw error;
    }
  },

  /**
   * Réinitialisation de mot de passe
   */
  resetPassword: async (email: string) => {
    addBreadcrumb({ category: 'auth', message: `Demande de réinitialisation: ${email}`, level: 'info' });
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase non configuré.');
    }
    const { error } = await auth.resetPassword(email);
    if (error) captureException(error, { phase: 'resetPassword', email });
  },

  /**
   * Sélection du type de profil (Porteur, Accompagnateur, etc.)
   */
  selectProfile: async (profileType: string) => {
    const { user } = get();
    if (!user) throw new Error('Aucun utilisateur trouvé');
    addBreadcrumb({ category: 'profile', message: `Profil sélectionné: ${profileType}`, level: 'info' });

    const { data: existingProfile } = await supabase
      .from('users')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    let error;
    if (existingProfile) {
      const { error: updateError } = await supabase
        .from('users')
        .update({ profile_type: profileType })
        .eq('id', user.id);
      error = updateError;
    } else {
      const { error: insertError } = await supabase
        .from('users')
        .insert({
          id: user.id,
          profile_type: profileType,
          full_name: user.full_name,
          phone: user.phone,
          organization: user.organization,
          address: user.address,
          city: user.city,
          postal_code: user.postal_code,
        });
      error = insertError;
    }

    if (error) {
      captureException(error, { phase: 'selectProfile', profileType });
      throw error;
    }

    Sentry.setTag('role', profileType);
    set({
      user: { ...user, profile_type: profileType as any },
      profileSelected: true,
    });
  },

  /**
   * Récupération du profil utilisateur complet (et auto-création si besoin)
   */
  fetchUser: async () => {
    set({ isLoading: true });
    try {
      if (!isSupabaseConfigured()) {
        console.warn('[AUTH STORE] Supabase non configuré');
        set({
          user: null,
          isAuthenticated: false,
          profileSelected: false,
          isLoading: false,
        });
        return;
      }

      const { user: authUser, error: authError } = await auth.getUser();
      if (authError) {
        captureException(authError, { phase: 'fetchUser' });
        await auth.signOut();
        set({
          user: null,
          isAuthenticated: false,
          profileSelected: false,
          isLoading: false,
        });
        return;
      }

      if (authUser) {
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .maybeSingle();

        if (profile) {
          // ✅ Profil trouvé
          setUserContext({
            id: profile.id,
            email: profile.email,
            profile_type: profile.profile_type,
          });
          Sentry.setTag('role', profile.profile_type);
          captureMessage(`Session restaurée: ${profile.email}`, 'info');
          set({
            user: profile,
            isAuthenticated: true,
            profileSelected: true,
            isLoading: false,
          });
        } else {
          // 🧱 Cas fallback (création auto si manquant)
          console.warn('[AUTH STORE] Utilisateur sans profil, tentative de création auto');
          const userEmail = authUser.email || '';
          const profileType = userEmail.includes('@cress-guadeloupe.org')
            ? 'ADMIN'
            : 'PORTEUR';

          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert({
              id: authUser.id,
              email: userEmail,
              profile_type: profileType,
              full_name: authUser.user_metadata?.full_name || null,
              phone: authUser.user_metadata?.phone || null,
            })
            .select()
            .single();

          if (createError) throw createError;

          setUserContext({
            id: newProfile.id,
            email: newProfile.email,
            profile_type: newProfile.profile_type,
          });
          Sentry.setTag('role', newProfile.profile_type);
          captureMessage(`Profil auto-créé: ${userEmail}`, 'info');
          set({
            user: newProfile,
            isAuthenticated: true,
            profileSelected: true,
            isLoading: false,
          });
        }
      } else {
        console.log('[AUTH STORE] Aucun utilisateur authentifié');
        set({
          user: null,
          isAuthenticated: false,
          profileSelected: false,
          isLoading: false,
        });
      }
    } catch (error) {
      captureException(error as Error, { phase: 'fetchUser' });
      await auth.signOut();
      set({
        user: null,
        isAuthenticated: false,
        profileSelected: false,
        isLoading: false,
      });
    }
  },

  /**
   * Mise à jour du profil utilisateur
   */
  updateProfile: async (updates: Partial<User>) => {
    const { user } = get();
    if (!user) throw new Error('Aucun utilisateur connecté');

    addBreadcrumb({ category: 'profile', message: 'Mise à jour du profil', level: 'info' });
    const { error } = await supabase.from('users').update(updates).eq('id', user.id);
    if (error) {
      captureException(error, { phase: 'updateProfile' });
      throw error;
    }

    set({ user: { ...user, ...updates } });
    captureMessage(`Profil mis à jour: ${user.email}`, 'info');
  },
}));
